import { useEffect, useState, useCallback } from 'react';

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

export interface TelegramWebAppData {
  user?: TelegramUser;
  chat_instance?: string;
  chat_type?: string;
  auth_date: number;
  hash: string;
}

export interface TelegramTheme {
  bg_color: string;
  text_color: string;
  hint_color: string;
  link_color: string;
  button_color: string;
  button_text_color: string;
  secondary_bg_color: string;
}

declare global {
  interface Window {
    Telegram: {
      WebApp: {
        initData: string;
        initDataUnsafe: TelegramWebAppData;
        version: string;
        platform: string;
        colorScheme: 'light' | 'dark';
        themeParams: TelegramTheme;
        isExpanded: boolean;
        viewportHeight: number;
        viewportStableHeight: number;
        headerColor: string;
        backgroundColor: string;
        isClosingConfirmationEnabled: boolean;
        BackButton: {
          isVisible: boolean;
          onClick(callback: () => void): void;
          offClick(callback: () => void): void;
          show(): void;
          hide(): void;
        };
        MainButton: {
          text: string;
          color: string;
          textColor: string;
          isVisible: boolean;
          isProgressVisible: boolean;
          isActive: boolean;
          onClick(callback: () => void): void;
          offClick(callback: () => void): void;
          setText(text: string): void;
          show(): void;
          hide(): void;
          enable(): void;
          disable(): void;
          showProgress(leaveActive?: boolean): void;
          hideProgress(): void;
        };
        HapticFeedback: {
          impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
          notificationOccurred(type: 'error' | 'success' | 'warning'): void;
          selectionChanged(): void;
        };
        expand(): void;
        close(): void;
        ready(): void;
        sendData(data: string): void;
        openLink(url: string): void;
        showPopup(params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: string; text: string }> }): void;
        showAlert(message: string): void;
        showConfirm(message: string): Promise<boolean>;
      };
    };
  }
}

export interface UseTelegramReturn {
  tg: typeof window.Telegram.WebApp | null;
  user: TelegramUser | null;
  isTelegramWebApp: boolean;
  isAuthenticated: boolean;
  authData: string;
  theme: TelegramTheme | null;
  colorScheme: 'light' | 'dark' | null;
  initializeWebApp: () => void;
  showAlert: (message: string) => void;
  showConfirm: (message: string) => Promise<boolean>;
  hapticFeedback: {
    impact: (style: 'light' | 'medium' | 'heavy') => void;
    notification: (type: 'error' | 'success' | 'warning') => void;
    selection: () => void;
  };
  validateAuthData: () => Promise<boolean>;
}

export const useTelegram = (): UseTelegramReturn => {
  const [isTelegramWebApp, setIsTelegramWebApp] = useState(false);
  const [user, setUser] = useState<TelegramUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authData, setAuthData] = useState('');
  const [theme, setTheme] = useState<TelegramTheme | null>(null);
  const [colorScheme, setColorScheme] = useState<'light' | 'dark' | null>(null);

  const parseUrlParams = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      user_id: urlParams.get('user_id'),
      username: urlParams.get('username'),
      timestamp: urlParams.get('timestamp'),
      auth: urlParams.get('auth')
    };
  }, []);

  const validateUrlAuth = useCallback((urlParams: any): boolean => {
    // Check if we have URL parameters from bot
    if (urlParams.user_id && urlParams.timestamp && urlParams.auth) {
      const timestamp = parseInt(urlParams.timestamp);
      const now = Math.floor(Date.now() / 1000);

      // Check if timestamp is within 10 minutes
      if (now - timestamp < 600) {
        console.log('✅ Valid URL authentication parameters found');
        return true;
      } else {
        console.log('⏰ URL authentication expired');
        return false;
      }
    }
    return false;
  }, []);

  const isTrueTelegramEnvironment = useCallback((tg: any): boolean => {
    // Check for real Telegram environment indicators
    const hasValidPlatform = tg.platform && tg.platform !== 'unknown';
    const hasThemeData = tg.themeParams && Object.keys(tg.themeParams).length > 0;
    const hasValidVersion = tg.version && parseFloat(tg.version) >= 6.0;

    return hasValidPlatform && hasThemeData && hasValidVersion;
  }, []);

  const validateAuthData = useCallback(async (): Promise<boolean> => {
    const tg = window.Telegram?.WebApp;
    const urlParams = parseUrlParams();

    if (!tg) {
      console.log('❌ No Telegram WebApp found');
      return false;
    }

    console.log('🔍 Telegram WebApp validation:', {
      hasWebApp: !!tg,
      platform: tg.platform,
      version: tg.version,
      initData: tg.initData,
      initDataLength: tg.initData?.length || 0,
      themeParamsLength: Object.keys(tg.themeParams || {}).length,
      hasUser: !!tg.initDataUnsafe?.user,
      urlParams: urlParams
    });

    // Check if this is a true Telegram environment
    const isTrueTelegram = isTrueTelegramEnvironment(tg);
    console.log('🔍 True Telegram environment check:', isTrueTelegram);

    // Priority 1: Standard Telegram auth with initData
    if (tg.initData && tg.initData.length > 0) {
      console.log('✅ Telegram WebApp validated with initData');
      return true;
    }

    // Priority 2: URL parameters from bot (for Solution 3)
    if (validateUrlAuth(urlParams)) {
      console.log('✅ URL authentication successful');
      // Set user data from URL params
      setUser({
        id: parseInt(urlParams.user_id!),
        first_name: urlParams.username || 'User',
        username: urlParams.username || undefined
      } as TelegramUser);
      return true;
    }

    // Priority 3: True Telegram environment (Desktop with theme)
    if (isTrueTelegram) {
      console.log('✅ True Telegram environment detected');
      return true;
    }

    // Development mode fallback ONLY if not in production
    if (process.env.NODE_ENV === 'development' && !isTrueTelegram) {
      console.warn('🔧 Development mode: Non-Telegram access detected - DENYING access for security');
      return false; // Changed: deny access to direct website visits
    }

    console.log('❌ No valid authentication found');
    return false;
  }, [parseUrlParams, validateUrlAuth, isTrueTelegramEnvironment]);

  const initializeWebApp = useCallback(() => {
    const tg = window.Telegram?.WebApp;

    if (!tg) {
      console.log('Telegram WebApp not available');
      return;
    }

    console.log('🚀 Initializing Telegram WebApp...');
    console.log('📊 WebApp Data:', {
      initData: tg.initData,
      initDataLength: tg.initData?.length || 0,
      user: tg.initDataUnsafe?.user,
      platform: tg.platform,
      version: tg.version,
      colorScheme: tg.colorScheme
    });

    // Initialize WebApp
    tg.ready();
    tg.expand();

    // Set state
    setIsTelegramWebApp(true);
    setUser(tg.initDataUnsafe?.user || null);
    setAuthData(tg.initData || '');
    setTheme(tg.themeParams || null);
    setColorScheme(tg.colorScheme || null);

    // Validate authentication
    validateAuthData().then(isValid => {
      setIsAuthenticated(isValid);
      if (isValid) {
        console.log('✅ Telegram authentication successful');
      } else {
        console.log('❌ Telegram authentication failed');
      }
    });

  }, [validateAuthData]);

  useEffect(() => {
    // Check if we're in Telegram environment
    const checkTelegram = () => {
      if (window.Telegram?.WebApp) {
        console.log('🔍 Telegram WebApp detected, initializing...');
        initializeWebApp();
      } else {
        console.log('🌐 No Telegram WebApp detected');

        // For development - allow non-Telegram access with warning
        const isDev = process.env.NODE_ENV === 'development';
        if (isDev) {
          console.warn('🔧 Development mode: Allowing access without Telegram');
          setIsAuthenticated(true);
          setIsTelegramWebApp(false);
        } else {
          console.log('❌ Not in Telegram WebApp environment');
          setIsAuthenticated(false);
          setIsTelegramWebApp(false);
        }
      }
    };

    // Check immediately and after a short delay (in case Telegram script loads later)
    checkTelegram();
    const timeout = setTimeout(() => {
      console.log('⏰ Timeout check for Telegram WebApp');
      checkTelegram();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [initializeWebApp]);

  const showAlert = useCallback((message: string) => {
    const tg = window.Telegram?.WebApp;
    if (tg) {
      tg.showAlert(message);
    } else {
      alert(message);
    }
  }, []);

  const showConfirm = useCallback(async (message: string): Promise<boolean> => {
    const tg = window.Telegram?.WebApp;
    if (tg && tg.showConfirm) {
      return await tg.showConfirm(message);
    } else {
      // eslint-disable-next-line no-restricted-globals
      return confirm(message);
    }
  }, []);

  const hapticFeedback = {
    impact: (style: 'light' | 'medium' | 'heavy') => {
      window.Telegram?.WebApp?.HapticFeedback?.impactOccurred(style);
    },
    notification: (type: 'error' | 'success' | 'warning') => {
      window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type);
    },
    selection: () => {
      window.Telegram?.WebApp?.HapticFeedback?.selectionChanged();
    }
  };

  return {
    tg: window.Telegram?.WebApp || null,
    user,
    isTelegramWebApp,
    isAuthenticated,
    authData,
    theme,
    colorScheme,
    initializeWebApp,
    showAlert,
    showConfirm,
    hapticFeedback,
    validateAuthData
  };
};